import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useEffect } from "react";
import { Bold, Italic, List, ListOrdered, Link2, Undo2, Redo2, Heading2 } from "lucide-react";

const Toolbar = ({ editor }) => {
  if (!editor) return null;
  const btn = (active, onClick, Icon, label, testid) => (
    <button
      type="button"
      onClick={onClick}
      className={`h-7 w-7 inline-flex items-center justify-center rounded border transition-colors ${
        active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
      }`}
      title={label}
      data-testid={testid}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
  const promptLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL du lien", prev || "https://");
    if (url === null) return;
    if (url === "") editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex items-center gap-1 border-b p-1.5 bg-muted/30 rounded-t-md flex-wrap">
      {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, "Gras", "editor-bold")}
      {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, "Italique", "editor-italic")}
      {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, "Titre", "editor-h2")}
      {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, "Liste", "editor-ul")}
      {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, "Liste numérotée", "editor-ol")}
      {btn(editor.isActive("link"), promptLink, Link2, "Lien", "editor-link")}
      <div className="mx-1 h-4 w-px bg-border" />
      {btn(false, () => editor.chain().focus().undo().run(), Undo2, "Annuler", "editor-undo")}
      {btn(false, () => editor.chain().focus().redo().run(), Redo2, "Rétablir", "editor-redo")}
    </div>
  );
};

/**
 * TipTap WYSIWYG editor with {{ variable }} insertion support.
 * - `value` : HTML string (controlled)
 * - `onChange(html)` called on each edit
 * - `variables` : array of available variable names (inserts {{ name }} at cursor)
 */
export const TiptapEditor = ({ value, onChange, variables = [], placeholder, testid }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener", target: "_blank" } }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none p-3 min-h-[160px] focus:outline-none",
        "data-testid": testid || "tiptap-editor",
      },
    },
  });

  // Sync external value changes (e.g. reset after save).
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const insertVariable = (name) => {
    editor?.chain().focus().insertContent(`{{ ${name} }}`).run();
  };

  return (
    <div className="border rounded-md bg-background">
      <Toolbar editor={editor} />
      {variables.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap p-1.5 border-b bg-muted/10">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Variables :</span>
          {variables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertVariable(v)}
              className="text-[11px] px-1.5 py-0.5 rounded bg-primary/10 hover:bg-primary/20 border border-primary/20 font-mono"
              data-testid={`editor-var-${v}`}
            >
              {"{{ " + v + " }}"}
            </button>
          ))}
        </div>
      )}
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
};

export default TiptapEditor;
